import { useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  FIX_ALL_PAGE_INDEXES,
  FIX_ALL_PAGE_TYPES
} from 'data/layoutcreator/mutations/migrations'
import { GET_PROJECT_DOCUMENTS } from 'data/layoutcreator/queries/documents'
import PageList from 'shared/components/PageList'
import LiveCursorWrapper from 'shared/components/PageList/components/LiveCursorWrapper'
import withRoom from 'shared/hocs/withRoom'
import { useLiveUsers } from 'shared/hooks/useLiveUsers'
import AdminLayout from 'shared/layout/Admin'
import Button from 'shared/ui/Button_2'
import Flex from 'shared/ui/Flex'
import Select from 'shared/ui/Select'

import Header from './components/Header'
import MissingPagePropertiesMigrationModal from './components/MissingPagePropertiesMigrationModal'
import PageMigrationsModal from './components/PageMigrationsModal'

const AdminProject: React.FC = () => {
  const { groupId, projectId } = useParams()
  const { formatMessage } = useIntl()
  const location = useLocation()
  const navigate = useNavigate()

  const isCoverView = location.pathname.includes('/cover')

  const [fixAllPageIndexes] = useMutation(FIX_ALL_PAGE_INDEXES)
  const [fixAllPageTypes] = useMutation(FIX_ALL_PAGE_TYPES)
  const [isPageMigrationsModalOpen, setIsPageMigrationsModalOpen] =
    useState(false)
  const [
    isMissingPagePropertiesModalOpen,
    setIsMissingPagePropertiesModalOpen
  ] = useState(false)
  const [migrationSelectIndex, setMigrationSelectIndex] = useState(0)
  const MIGRATION_SELECT_ITEMS = [
    {
      name: formatMessage({
        id: 'AdminProject.fields.migrations.pageMigration'
      }),
      action: () => {
        setIsPageMigrationsModalOpen(true)
      }
    },
    {
      name: 'Fix missing page properties for a project',
      action: () => {
        setIsMissingPagePropertiesModalOpen(true)
      }
    },
    {
      name: 'Fix page indexes for all projects',
      action: async () => {
        const loadingToast = toast.loading('Fixing page indexes...')
        try {
          await fixAllPageIndexes()
          toast.success('Page indexes fixed for all projects', {
            id: loadingToast
          })
        } catch (e) {
          toast.error('Error fixing page indexes', {
            id: loadingToast
          })
        }
      }
    },
    {
      name: 'Fix page types for all projects',
      action: async () => {
        const loadingToast = toast.loading('Fixing page types...')
        try {
          await fixAllPageTypes()
          toast.success('Page types fixed for all projects', {
            id: loadingToast
          })
        } catch (e) {
          toast.error('Error fixing page types', { id: loadingToast })
        }
      }
    }
  ]

  const { data } = useQuery(GET_PROJECT_DOCUMENTS, {
    variables: {
      groupId: groupId!,
      projectId: projectId!
    }
  })

  const liveUsers = useLiveUsers()

  const projectDataFields: {
    title: string
    value: string
    hide?: boolean
  }[] = [
    { title: 'ID', value: projectId! },
    {
      title: formatMessage({ id: 'AdminProject.fields.groupId' }),
      value: groupId!
    },
    {
      title: formatMessage({ id: 'AdminProject.fields.pagesCount' }),
      value: data?.projectDocuments.length.toString() || '',
      hide: !data?.projectDocuments
    }
  ]

  const handleViewSwitch = (view: 'content' | 'cover') => {
    if (view === 'content') {
      navigate(`/admin/projects/${groupId}/${projectId}/content`)
    } else {
      navigate(`/admin/projects/${groupId}/${projectId}/cover`)
    }
  }

  return (
    <AdminLayout
      showFullscreenButton={false}
      renderHeaderContent={() => (
        <Header
          liveUsers={liveUsers}
          currentView={isCoverView ? 'cover' : 'content'}
          onViewSwitch={handleViewSwitch}
        />
      )}
    >
      <LiveCursorWrapper>
        <Flex
          direction="column"
          alignItems="flex-start"
          marginTop={30}
        >
          <Flex
            width="100%"
            justifyContent="space-between"
            alignItems="center"
          >
            <Flex direction="column" rowGap={3}>
              {projectDataFields.map((item, i) => (
                <span key={i}>
                  <strong>{item.title}: </strong>
                  <span>{item.value}</span>
                </span>
              ))}
            </Flex>

            <Flex columnGap={8}>
              <Select
                width={230}
                options={MIGRATION_SELECT_ITEMS.map(
                  item => item.name
                )}
                activeIndex={migrationSelectIndex}
                onChange={(_, index) =>
                  setMigrationSelectIndex(index)
                }
              />
              <Button
                textId="AdminProject.buttons.runFix"
                intent="secondary-contrast"
                size="md"
                maxHeight={34}
                onPress={() =>
                  MIGRATION_SELECT_ITEMS[
                    migrationSelectIndex
                  ].action()
                }
              />
            </Flex>
          </Flex>

          <Flex
            width="100%"
            justifyContent="center"
            marginTop={34}
            marginBottom={128}
          >
            <PageList projectId={projectId!} groupId={groupId!} />
          </Flex>
        </Flex>
      </LiveCursorWrapper>

      <PageMigrationsModal
        isOpen={isPageMigrationsModalOpen}
        closeModal={() => setIsPageMigrationsModalOpen(false)}
      />

      <MissingPagePropertiesMigrationModal
        isOpen={isMissingPagePropertiesModalOpen}
        closeModal={() => setIsMissingPagePropertiesModalOpen(false)}
      />
    </AdminLayout>
  )
}

export default withRoom(AdminProject)
